import {
  AUDIT_SOURCE_LABELS,
  AUDIT_SOURCES,
  REPORT_EXPORT_STATUS_LABELS,
  REPORT_FORMAT_LABELS,
  REPORTS,
  actionPlanOptionsSchema,
  auditOptionsSchema,
  formatDateTime,
  initiativeOptionsSchema,
  kpiOptionsSchema,
  localIsoDay,
  meetingListSchema,
  objectiveOptionsSchema,
  perspectiveDashboardSchema,
  plannedOptionsSchema,
  reportExportListSchema,
  reportFileName,
  reportQueuedSchema,
  targetOptionsSchema,
  targetPeriodLabel,
  type ReportDefinition,
  type ReportExport,
  type ReportFormat,
  type ReportKey,
  type WorkspaceProject,
} from '@fluxus/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Download, FileBarChart, FolderKanban } from 'lucide-react';
import { useId, useState } from 'react';
import { useParams, useSearchParams } from 'react-router';
import { toast } from 'sonner';
import { PageContainer, PageHeader } from '@/components/layout/PageHeader';
import { EmptyState, ErrorState, LoadingState } from '@/components/states/States';
import { Button } from '@/components/ui/button';
import { FormField } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { NativeSelect } from '@/components/ui/select';
import { ProjectPicker, useCurrentProject } from '@/features/workspace/workspace';
import { errorMessage } from '@/lib/api/errors';
import { useAuth, useCan } from '@/lib/auth/session';
import { saveBlobFile } from '@/lib/platform';

type Ref = { id: string; name: string };
const MONTHS = Array.from({ length: 12 }, (_, i) => i + 1);

/** Junta listas de várias fontes sem repetir (por id). Sem nenhuma fonte acessível: null (o filtro sai da tela). */
function merge(...lists: (Ref[] | undefined)[]): Ref[] | null {
  const present = lists.filter((l): l is Ref[] => Boolean(l));
  if (!present.length) return null;
  const seen = new Map<string, Ref>();
  for (const list of present)
    for (const r of list) if (!seen.has(r.id)) seen.set(r.id, { id: r.id, name: r.name });
  return [...seen.values()].sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'));
}
const sizeText = (bytes: number | null) =>
  bytes === null
    ? ''
    : bytes < 1024 * 1024
      ? `${Math.max(1, Math.round(bytes / 1024))} KB`
      : `${(bytes / 1024 / 1024).toFixed(1).replace('.', ',')} MB`;

/**
 * Relatórios (T-201d, RF-44, RN-24, PRD §11.29; DEC-068): os relatórios que o perfil pode exportar, os filtros de cada
 * um (com as opções das telas que a pessoa vê) e os formatos; baixa na hora ou acompanha o relatório grande gerado
 * na fila até ficar pronto.
 */
export function RelatoriosPage() {
  const { organizationId = '' } = useParams();
  const can = useCan();
  const { workspace, projects, project, select } = useCurrentProject(organizationId);
  const available = REPORTS.filter((r) => can(r.module, 'view'));
  return (
    <PageContainer className="max-w-6xl">
      <PageHeader
        title="Relatórios"
        description="Escolha o relatório, os filtros e o formato: PDF com o logotipo da empresa, Excel ou CSV. Relatórios grandes são gerados em segundo plano e ficam disponíveis para baixar."
      />
      {workspace.isPending ? (
        <LoadingState className="mt-6" rows={4} label="Carregando o projeto" />
      ) : workspace.isError ? (
        <ErrorState
          className="mt-6"
          error={workspace.error}
          onRetry={() => void workspace.refetch()}
          retrying={workspace.isFetching}
        />
      ) : !available.length ? (
        <EmptyState
          className="mt-6 rounded-lg border border-dashed border-border py-12"
          icon={FileBarChart}
          title="Nenhum relatório disponível"
          description="Seu perfil não vê os módulos dos relatórios. Peça a quem administra a empresa."
        />
      ) : (
        <div className="mt-4 flex flex-col gap-6">
          {projects.length ? <ProjectPicker projects={projects} project={project} onSelect={select} /> : null}
          <ReportBuilder organizationId={organizationId} project={project} reports={available} />
          <GeneratedReports organizationId={organizationId} />
        </div>
      )}
    </PageContainer>
  );
}

function ReportBuilder({
  organizationId,
  project,
  reports,
}: {
  organizationId: string;
  project: WorkspaceProject | null;
  reports: readonly ReportDefinition[];
}) {
  const [params, setParams] = useSearchParams();
  const selectedKey = (params.get('relatorio') as ReportKey | null) ?? reports[0]!.key;
  const def = reports.find((r) => r.key === selectedKey) ?? reports[0]!;
  const choose = (key: ReportKey) =>
    setParams(
      (p) => {
        const next = new URLSearchParams(p);
        next.set('relatorio', key);
        return next;
      },
      { replace: true },
    );
  return (
    <div className="grid gap-4 lg:grid-cols-[18rem_1fr]">
      <nav aria-label="Relatórios disponíveis">
        <ul className="flex flex-col gap-1">
          {reports.map((r) => (
            <li key={r.key}>
              <button
                type="button"
                aria-current={r.key === def.key ? 'true' : undefined}
                onClick={() => choose(r.key)}
                className={`min-h-11 w-full rounded-md px-3 py-2 text-left text-sm ${r.key === def.key ? 'bg-brand/10 font-semibold text-brand-strong dark:text-brand' : 'hover:bg-muted'}`}
              >
                {r.label}
              </button>
            </li>
          ))}
        </ul>
      </nav>
      {def.project && !project ? (
        <EmptyState
          className="rounded-lg border border-dashed border-border py-12"
          icon={FolderKanban}
          title="Nenhum projeto BSC"
          description="Este relatório é de um projeto. Quando a empresa tiver um projeto, ele aparece aqui."
        />
      ) : (
        <ReportForm
          key={`${def.key}|${project?.id ?? ''}`}
          organizationId={organizationId}
          project={project}
          def={def}
        />
      )}
    </div>
  );
}

function useOptions(organizationId: string, projectId: string | null, def: ReportDefinition) {
  const { client } = useAuth();
  const can = useCan();
  const pbase = `/organizations/${organizationId}/projects/${projectId}`;
  const needs = (f: string) => def.filters.includes(f as never);
  const wantAreaLists =
    needs('perspective') || needs('objective') || needs('department') || needs('businessUnit');
  const on = (module: Parameters<typeof can>[0]) => Boolean(projectId) && can(module, 'view');
  const kpis = useQuery({
    queryKey: ['report-options', projectId, 'kpis'],
    enabled: wantAreaLists && on('indicadores'),
    queryFn: ({ signal }) => client.request(`${pbase}/kpis/options`, { schema: kpiOptionsSchema, signal }),
  });
  const targets = useQuery({
    queryKey: ['report-options', projectId, 'targets'],
    enabled: wantAreaLists && on('metas-resultados'),
    queryFn: ({ signal }) =>
      client.request(`${pbase}/targets/options`, { schema: targetOptionsSchema, signal }),
  });
  const planned = useQuery({
    queryKey: ['report-options', projectId, 'planned'],
    enabled: wantAreaLists && on('previsto-realizado'),
    queryFn: ({ signal }) =>
      client.request(`${pbase}/planned-vs-actual/options`, { schema: plannedOptionsSchema, signal }),
  });
  // Objetivos (perspectivas, objetivos e áreas): a fonte de quem vê o módulo Objetivos (relatório por objetivo).
  const objectives = useQuery({
    queryKey: ['report-options', projectId, 'objectives'],
    enabled: wantAreaLists && on('objetivos'),
    queryFn: ({ signal }) =>
      client.request(`${pbase}/objectives/options`, { schema: objectiveOptionsSchema, signal }),
  });
  // Perspectivas do dashboard (relatório por perspectiva, para quem só vê o Dashboard por Perspectiva).
  const perspectiveDashboard = useQuery({
    queryKey: ['report-options', projectId, 'perspective-dashboard'],
    enabled: needs('perspective') && on('dashboard-perspectiva'),
    queryFn: ({ signal }) =>
      client.request(`${pbase}/perspective-dashboard`, { schema: perspectiveDashboardSchema, signal }),
  });
  const initiatives = useQuery({
    queryKey: ['report-options', projectId, 'initiatives'],
    enabled: (wantAreaLists || needs('responsible')) && on('iniciativas'),
    queryFn: ({ signal }) =>
      client.request(`${pbase}/initiatives/options`, { schema: initiativeOptionsSchema, signal }),
  });
  const plans = useQuery({
    queryKey: ['report-options', projectId, 'plans'],
    enabled: (wantAreaLists || needs('responsible')) && on('planos-acao'),
    queryFn: ({ signal }) =>
      client.request(`${pbase}/action-plans/options`, { schema: actionPlanOptionsSchema, signal }),
  });
  const meetings = useQuery({
    queryKey: ['report-options', projectId, 'meetings'],
    enabled: needs('meeting') && on('reunioes-decisoes'),
    queryFn: ({ signal }) =>
      client.request(`${pbase}/meetings`, {
        schema: meetingListSchema,
        signal,
        query: { status: 'CLOSED', type: 'CRITICAL_ANALYSIS', order: 'desc', pageSize: '100' },
      }),
  });
  const audit = useQuery({
    queryKey: ['report-options', organizationId, 'audit'],
    enabled: (needs('auditUser') || needs('auditEntity')) && can('auditoria', 'view'),
    queryFn: ({ signal }) =>
      client.request(`/organizations/${organizationId}/audit/options`, {
        schema: auditOptionsSchema,
        signal,
      }),
  });
  const all = [kpis, targets, planned, objectives, perspectiveDashboard, initiatives, plans, meetings, audit];
  return {
    isPending: all.some((q) => q.isLoading),
    error: all.find((q) => q.isError)?.error ?? null,
    retry: () => all.forEach((q) => q.isError && void q.refetch()),
    perspectives: merge(
      objectives.data?.perspectives,
      kpis.data?.perspectives,
      planned.data?.perspectives,
      initiatives.data?.perspectives,
      perspectiveDashboard.data?.perspectives,
    ),
    objectives: merge(
      objectives.data?.objectives,
      kpis.data?.objectives,
      planned.data?.objectives,
      initiatives.data?.objectives,
    ),
    departments: merge(
      objectives.data?.departments,
      kpis.data?.departments,
      targets.data?.departments,
      planned.data?.departments,
      plans.data?.departments,
    ),
    units: merge(targets.data?.businessUnits, planned.data?.units, plans.data?.businessUnits),
    people: merge(plans.data?.people, initiatives.data?.people),
    meetings: meetings.data
      ? meetings.data.items.map((m) => ({ id: m.id, name: `${m.title} (${formatDateTime(m.startsAt)})` }))
      : null,
    /** Mais análises encerradas do que a lista traz (as mais recentes primeiro). */
    meetingsTotal: meetings.data?.total ?? 0,
    auditUsers: audit.data?.users ?? null,
    auditEntities: audit.data?.entities ?? null,
  };
}

function ReportForm({
  organizationId,
  project,
  def,
}: {
  organizationId: string;
  project: WorkspaceProject | null;
  def: ReportDefinition;
}) {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  const projectId = def.project ? (project?.id ?? null) : null;
  const options = useOptions(organizationId, projectId, def);
  const today = localIsoDay();
  const thisYear = Number(today.slice(0, 4));
  const years = project
    ? Array.from(
        { length: project.horizonEnd - project.horizonStart + 1 },
        (_, i) => project.horizonStart + i,
      )
    : [thisYear];
  const [values, setValues] = useState<Record<string, string>>(() => ({
    year: String(years.includes(thisYear) ? thisYear : years[0]),
    month: String(Number(today.slice(5, 7))),
  }));
  const [format, setFormat] = useState<ReportFormat>(def.formats[0]!);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const set = (k: string, v: string) => setValues((s) => ({ ...s, [k]: v }));
  const ids = { year: useId(), month: useId(), from: useId(), to: useId(), dueFrom: useId(), dueTo: useId() };
  const has = (f: string) => def.filters.includes(f as never);

  const buildQuery = () => {
    const q: Record<string, string> = { format };
    if (projectId) q.projectId = projectId;
    if (has('period')) Object.assign(q, { year: values.year, month: values.month });
    if (has('year')) q.year = values.year!;
    const pick: [string, string][] = [
      ['perspective', 'perspectiveId'],
      ['objective', 'objectiveId'],
      ['department', 'departmentId'],
      ['businessUnit', 'businessUnitId'],
      ['responsible', 'responsibleId'],
      ['meeting', 'meetingId'],
      ['auditUser', 'userId'],
      ['auditEntity', 'entity'],
      ['auditSource', 'source'],
    ];
    for (const [filter, field] of pick) if (has(filter) && values[field]) q[field] = values[field]!;
    if (has('range')) {
      if (values.from) q.from = values.from;
      if (values.to) q.to = values.to;
    }
    if (has('due')) {
      if (values.dueFrom) q.dueFrom = values.dueFrom;
      if (values.dueTo) q.dueTo = values.dueTo;
    }
    return q;
  };

  const generate = useMutation({
    meta: { handlesError: true },
    mutationFn: async () => {
      const result = await client.fileOrQueued(`/organizations/${organizationId}/reports/${def.key}`, {
        query: buildQuery(),
      });
      if (result.kind === 'file') {
        await saveBlobFile({
          fileName: reportFileName(def.key, format, localIsoDay()),
          title: def.label,
          blob: result.blob,
        });
        return 'file' as const;
      }
      const queued = reportQueuedSchema.parse(result.body);
      toast.info(queued.message);
      await queryClient.invalidateQueries({ queryKey: ['report-exports', organizationId] });
      return 'queued' as const;
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  // Filtro obrigatório sem nenhuma lista que o perfil veja: a pessoa precisa saber por que não dá para gerar.
  const missingSource = [
    def.required?.includes('objective') && !options.objectives
      ? 'Este relatório precisa de um objetivo, e seu perfil não vê a lista de objetivos (Objetivos, Indicadores, Previsto x Realizado ou Iniciativas).'
      : null,
    def.required?.includes('meeting') && !options.meetings
      ? 'Este relatório precisa de uma análise crítica encerrada, e seu perfil não vê as reuniões (Reuniões e Decisões).'
      : null,
  ].filter((m): m is string => Boolean(m));

  const validate = () => {
    const e: Record<string, string> = {};
    if (def.required?.includes('objective') && !values.objectiveId)
      e.objectiveId = 'Escolha o objetivo do relatório.';
    if (def.required?.includes('meeting') && !values.meetingId)
      e.meetingId = 'Escolha a reunião do relatório.';
    if (values.from && values.to && values.from > values.to) e.to = 'O fim vem depois do início.';
    if (values.dueFrom && values.dueTo && values.dueFrom > values.dueTo)
      e.dueTo = 'O fim vem depois do início.';
    setErrors(e);
    return !Object.keys(e).length;
  };

  const selectField = (filter: string, field: string, label: string, list: Ref[] | null, empty: string) =>
    has(filter) && list ? (
      <FormField key={field} id={`${def.key}-${field}`} label={label} error={errors[field]}>
        <NativeSelect
          id={`${def.key}-${field}`}
          value={values[field] ?? ''}
          onChange={(e) => set(field, e.target.value)}
        >
          <option value="">{empty}</option>
          {list.map((o) => (
            <option key={o.id} value={o.id}>
              {o.name}
            </option>
          ))}
        </NativeSelect>
      </FormField>
    ) : null;

  return (
    <section aria-label={`Relatório ${def.label}`} className="rounded-lg border border-border p-4">
      <h2 className="text-lg font-semibold">{def.label}</h2>
      <p className="mt-1 text-sm text-muted-foreground">{def.description}</p>
      {options.isPending ? (
        <LoadingState className="mt-4" rows={2} label="Carregando os filtros" />
      ) : options.error ? (
        <ErrorState className="mt-4" error={options.error} onRetry={options.retry} />
      ) : (
        <form
          className="mt-4 flex flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (validate() && !generate.isPending) generate.mutate();
          }}
        >
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {has('period') || has('year') ? (
              <FormField id={ids.year} label="Ano">
                <NativeSelect id={ids.year} value={values.year} onChange={(e) => set('year', e.target.value)}>
                  {years.map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </NativeSelect>
              </FormField>
            ) : null}
            {has('period') ? (
              <FormField id={ids.month} label="Mês">
                <NativeSelect
                  id={ids.month}
                  value={values.month}
                  onChange={(e) => set('month', e.target.value)}
                >
                  {MONTHS.map((m) => (
                    <option key={m} value={m}>
                      {targetPeriodLabel('MONTH', m)}
                    </option>
                  ))}
                </NativeSelect>
              </FormField>
            ) : null}
            {selectField('perspective', 'perspectiveId', 'Perspectiva', options.perspectives, 'Todas')}
            {selectField(
              'objective',
              'objectiveId',
              'Objetivo',
              options.objectives,
              def.required?.includes('objective') ? 'Escolha o objetivo' : 'Todos',
            )}
            {selectField('department', 'departmentId', 'Área', options.departments, 'Todas')}
            {selectField(
              'businessUnit',
              'businessUnitId',
              'Unidade',
              options.units,
              def.key === 'previsto-realizado' ? 'Consolidado' : 'Todas',
            )}
            {selectField('responsible', 'responsibleId', 'Responsável', options.people, 'Todos')}
            {selectField('meeting', 'meetingId', 'Análise crítica', options.meetings, 'Escolha a reunião')}
            {has('meeting') && options.meetings && options.meetingsTotal > options.meetings.length ? (
              <p className="text-xs text-muted-foreground sm:col-span-2 lg:col-span-3">
                Mostrando as {options.meetings.length} análises críticas encerradas mais recentes de
                {` ${options.meetingsTotal}`}: para uma mais antiga, abra a ata pela tela de reuniões.
              </p>
            ) : null}
            {selectField('auditUser', 'userId', 'Quem alterou', options.auditUsers, 'Todos')}
            {has('auditEntity') && options.auditEntities ? (
              <FormField id={`${def.key}-entity`} label="Entidade">
                <NativeSelect
                  id={`${def.key}-entity`}
                  value={values.entity ?? ''}
                  onChange={(e) => set('entity', e.target.value)}
                >
                  <option value="">Todas</option>
                  {options.auditEntities.map((o) => (
                    <option key={o.key} value={o.key}>
                      {o.label}
                    </option>
                  ))}
                </NativeSelect>
              </FormField>
            ) : null}
            {has('auditSource') ? (
              <FormField id={`${def.key}-source`} label="Origem">
                <NativeSelect
                  id={`${def.key}-source`}
                  value={values.source ?? ''}
                  onChange={(e) => set('source', e.target.value)}
                >
                  <option value="">Todas</option>
                  {AUDIT_SOURCES.map((s) => (
                    <option key={s} value={s}>
                      {AUDIT_SOURCE_LABELS[s]}
                    </option>
                  ))}
                </NativeSelect>
              </FormField>
            ) : null}
            {has('range') ? (
              <>
                <FormField id={ids.from} label="De">
                  <Input
                    id={ids.from}
                    type="date"
                    className="h-11"
                    value={values.from ?? ''}
                    onChange={(e) => set('from', e.target.value)}
                  />
                </FormField>
                <FormField id={ids.to} label="Até" error={errors.to}>
                  <Input
                    id={ids.to}
                    type="date"
                    className="h-11"
                    value={values.to ?? ''}
                    onChange={(e) => set('to', e.target.value)}
                  />
                </FormField>
              </>
            ) : null}
            {has('due') ? (
              <>
                <FormField id={ids.dueFrom} label="Prazo de">
                  <Input
                    id={ids.dueFrom}
                    type="date"
                    className="h-11"
                    value={values.dueFrom ?? ''}
                    onChange={(e) => set('dueFrom', e.target.value)}
                  />
                </FormField>
                <FormField id={ids.dueTo} label="Prazo até" error={errors.dueTo}>
                  <Input
                    id={ids.dueTo}
                    type="date"
                    className="h-11"
                    value={values.dueTo ?? ''}
                    onChange={(e) => set('dueTo', e.target.value)}
                  />
                </FormField>
              </>
            ) : null}
          </div>
          <fieldset>
            <legend className="text-sm font-medium">Formato</legend>
            <div className="mt-2 flex flex-wrap gap-2">
              {def.formats.map((f) => (
                <label
                  key={f}
                  className="flex min-h-11 cursor-pointer items-center gap-2 rounded-md border border-border px-3"
                >
                  <input
                    type="radio"
                    name={`${def.key}-format`}
                    value={f}
                    checked={format === f}
                    onChange={() => setFormat(f)}
                  />
                  {REPORT_FORMAT_LABELS[f]}
                </label>
              ))}
            </div>
          </fieldset>
          {missingSource.map((message) => (
            <p key={message} role="alert" className="rounded-md border border-border bg-muted/40 p-3 text-sm">
              {message} Peça a quem administra a empresa.
            </p>
          ))}
          <div>
            <Button type="submit" loading={generate.isPending} disabled={missingSource.length > 0}>
              <Download aria-hidden />
              Gerar {REPORT_FORMAT_LABELS[format]}
            </Button>
          </div>
        </form>
      )}
    </section>
  );
}

function GeneratedReports({ organizationId }: { organizationId: string }) {
  const { client } = useAuth();
  const base = `/organizations/${organizationId}/reports/exports`;
  const list = useQuery({
    queryKey: ['report-exports', organizationId],
    queryFn: ({ signal }) => client.request(base, { schema: reportExportListSchema, signal }),
    // Enquanto algum relatório está na fila ou sendo gerado, a lista se atualiza sozinha.
    refetchInterval: (q) =>
      q.state.data?.items.some((e) => e.status === 'QUEUED' || e.status === 'PROCESSING') ? 3000 : false,
  });
  const download = useMutation({
    meta: { handlesError: true },
    mutationFn: async (e: ReportExport) =>
      saveBlobFile({
        fileName: e.fileName!,
        title: e.label,
        blob: await client.download(`${base}/${e.id}/file`),
      }),
    onError: (err) => toast.error(errorMessage(err)),
  });
  return (
    <section aria-label="Relatórios gerados em segundo plano">
      <h2 className="text-lg font-semibold">Relatórios gerados</h2>
      <p className="text-sm text-muted-foreground">
        Os relatórios grandes que você pediu: ficam disponíveis para baixar por alguns dias.
      </p>
      {list.isPending ? (
        <LoadingState className="mt-3" rows={2} label="Carregando os relatórios gerados" />
      ) : list.isError ? (
        <ErrorState
          className="mt-3"
          error={list.error}
          onRetry={() => void list.refetch()}
          retrying={list.isFetching}
        />
      ) : !list.data.items.length ? (
        <p className="mt-3 text-sm text-muted-foreground">Nenhum relatório gerado em segundo plano.</p>
      ) : (
        <ul className="mt-3 flex flex-col gap-2">
          {list.data.items.map((e) => {
            // Pronto, mas o prazo para baixar já passou (a varredura apaga depois): sem o botão.
            const expired = e.status === 'DONE' && !!e.expiresAt && Date.parse(e.expiresAt) < Date.now();
            return (
              <li
                key={e.id}
                className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border p-3 text-sm"
              >
                <div className="min-w-0">
                  <p className="font-medium">
                    {e.label} · {REPORT_FORMAT_LABELS[e.format]}
                  </p>
                  <p className="break-words text-xs text-muted-foreground">{e.subtitle}</p>
                  <p className="text-xs text-muted-foreground">
                    Pedido em {formatDateTime(e.createdAt)} ·{' '}
                    {expired ? 'Vencido: gere de novo' : REPORT_EXPORT_STATUS_LABELS[e.status]}
                    {e.status === 'DONE' && e.expiresAt && !expired
                      ? ` · disponível até ${formatDateTime(e.expiresAt)}`
                      : ''}
                    {e.size !== null ? ` · ${sizeText(e.size)}` : ''}
                  </p>
                  {e.error ? <p className="text-xs text-status-red-fg">{e.error}</p> : null}
                </div>
                {e.status === 'DONE' && !expired ? (
                  <Button
                    variant="outline"
                    size="sm"
                    loading={download.isPending && download.variables?.id === e.id}
                    onClick={() => download.mutate(e)}
                  >
                    <Download aria-hidden />
                    Baixar
                  </Button>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
